import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FilterListIcon from '@mui/icons-material/FilterList';
import { backgroundColors } from '@/tokens/colors';

/**
 * FilterPanel
 * Classification: custom
 *
 * A container for filter controls (Search, Select, severity Chips...) with a
 * consistent header and apply/clear footer. Compose filter inputs as children.
 */
export interface FilterPanelProps {
  children: ReactNode;
  title?: string;
  onApply?: () => void;
  onClear?: () => void;
  /** Active filter count shown in the header. */
  activeCount?: number;
}

export function FilterPanel({
  children,
  title = 'Filters',
  onApply,
  onClear,
  activeCount = 0,
}: FilterPanelProps) {
  return (
    <Box
      sx={{
        width: 280,
        bgcolor: backgroundColors.surface,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterListIcon fontSize="small" />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {title}
        </Typography>
        {activeCount > 0 ? (
          <Typography variant="caption" color="primary">
            {activeCount} active
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</Box>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button variant="ghost" size="small" onClick={onClear}>
          Clear
        </Button>
        <Button variant="primary" size="small" onClick={onApply}>
          Apply
        </Button>
      </Box>
    </Box>
  );
}
